import { Injectable } from "@angular/core";
import { AccessLinkAuthResponse } from "../interfaces/access-link-auth-response";

/**
 * Mock service per AccessLinkController.
 * Simula le risposte delle API AccessPage/Auth e AccessPage/UploadFile
 * per permettere lo sviluppo e il test della pagina AccessLink
 * in assenza di backend.
 *
 * ATTIVAZIONE: impostare `accessLinkMock: true` in environment.ts.
 * DISATTIVAZIONE: impostare `accessLinkMock: false` oppure rimuovere la proprietà.
 *
 * Per simulare un errore 400, impostare `accessLinkMockError: true` in environment.ts.
 */
@Injectable({
  providedIn: "root"
})
export class AccessLinkMockService {

  /** Messaggio restituito nel mock di errore (400) */
  private readonly MOCK_ERROR_MESSAGE: string =
    "Il link non è valido o è già stato utilizzato. Contatta il supporto.";

  /**
   * Costruisce la risposta mock al momento della chiamata.
   * Session viene calcolato dinamicamente come datetime ISO 3 minuti nel futuro,
   * così ogni invocazione restituisce un timer fresco e realistico.
   */
  private buildMockResponse(): AccessLinkAuthResponse {

    // 3 minuti nel futuro — valore basso per testare agevolmente il timeout
    const sessionExpiresAt = new Date(Date.now() + 3 * 60 * 1000).toISOString();

    return {
      CompanyCode: "AXPOITALIA",
      Upload: true,
      Session: sessionExpiresAt,
      Downloads: [
        {
          Type: "PDF",
          Endpoint: "https://www.w3.org/WAI/WCAG21/Techniques/pdf/PDF2.pdf",
          Filename: "Termsheet_Contratto.pdf",
          Description: "Documento contrattuale da leggere e scaricare prima della firma."
        },
        {
          Type: "WORD",
          Endpoint: "https://file-examples.com/storage/fef47bc0f163b23718a1016/2017/02/file-sample_100kB.docx",
          Filename: "Allegato_Tecnico.docx",
          Description: "Allegato tecnico con specifiche di fornitura."
        }
      ]
    };

  }

  /**
   * Simula la chiamata GET AccessPage/Auth.
   * Rispetta un delay realistico di rete.
   * @param shouldError - Se true simula un 400
   */
  auth(shouldError: boolean = false): Promise<AccessLinkAuthResponse> {

    return new Promise<AccessLinkAuthResponse>((resolve, reject) => {

      setTimeout(() => {

        if (shouldError) {

          reject({ error: { Message: this.MOCK_ERROR_MESSAGE } });

        } else {

          resolve(this.buildMockResponse());

        }

      }, 600);

    });

  }

  /**
   * Simula la chiamata POST AccessPage/UploadFile.
   * @param file  - File selezionato dall'utente
   * @param token - Token della sessione
   */
  uploadFile(file: File, token: string): Promise<boolean> {

    return new Promise<boolean>((resolve) => {

      console.log(`[AccessLinkMock] uploadFile — filename: ${file.name}, token: ${token}`);

      setTimeout(() => resolve(true), 800);

    });

  }

}
